/**
 * Release notes — the rows behind the in-app "What's New" page (/whats-new).
 *
 * Backed by `public.release_notes` (migration 2026-09-24-009). One row per user-facing change,
 * written functionally: what a person can now do, or what stopped happening to them.
 *
 * ⚠️ `category`, `audience` and `status` are CHECK-constrained in the database. These unions
 * mirror those CHECKs; if one changes there, change it here in the same migration. The page
 * degrades gracefully on an unknown value (it falls back to a neutral badge and the raw text)
 * rather than crashing, because the DB is the authority and a deploy can lag a migration.
 */

export type ReleaseNoteCategory =
  | "events"
  | "contacts"
  | "support"
  | "campaigns"
  | "privacy"
  | "platform";

export type ReleaseNoteAudience =
  | "everyone"
  | "event_operators"
  | "support_team"
  | "marketing"
  | "administrators";

export type ReleaseNoteStatus = "draft" | "published";

export interface ReleaseNote {
  id: string;
  /** The release this note belongs to; several notes share one version. */
  version: string;
  /** Stable identifier, also the page's anchor target. */
  slug: string;
  title: string;
  /** The functional paragraph shown on the card. */
  summary: string;
  category: ReleaseNoteCategory | string;
  audience: ReleaseNoteAudience | string;
  status: ReleaseNoteStatus | string;
  /** Date shown on the page. Always present for a published note (DB CHECK). */
  published_at: string | null;
  /** Ordering WITHIN a version, lowest first. */
  sort_order: number;
  created_at: string;
  updated_at: string;
}

/** One release's worth of notes, as the page renders them. */
export interface ReleaseNoteGroup {
  version: string;
  /** The newest published_at within the group; used for the heading and for sorting. */
  published_at: string | null;
  notes: ReleaseNote[];
}

export const RELEASE_NOTE_CATEGORY_LABELS: Record<string, string> = {
  events: "Events",
  contacts: "Contacts",
  support: "Support",
  campaigns: "Campaigns",
  privacy: "Privacy & access",
  platform: "Platform",
};

export const RELEASE_NOTE_AUDIENCE_LABELS: Record<string, string> = {
  everyone: "Everyone",
  event_operators: "Event operators",
  support_team: "Support team",
  marketing: "Marketing",
  administrators: "Administrators",
};
